import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Wallet, ArrowClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_BASE = window.location.origin;

function fmt(n) {
  if (typeof n !== 'number') n = Number(n || 0);
  return '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
}

export default function AdminFundRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [showApprove, setShowApprove] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [active, setActive] = useState(null);
  const [approveForm, setApproveForm] = useState({ txn_reference: '', review_notes: '' });
  const [rejectNotes, setRejectNotes] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filter) p.set('status', filter);
      const r = await api.get(`/vendor-fund-requests/?${p.toString()}`);
      setRequests(r.data || []);
    } catch (err) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openApprove = (r) => { setActive(r); setApproveForm({ txn_reference: '', review_notes: '' }); setShowApprove(true); };
  const doApprove = async () => {
    try {
      await api.post(`/vendor-fund-requests/${active.id}/approve`, approveForm);
      toast.success('Approved + ledger debited');
      setShowApprove(false);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const openReject = (r) => { setActive(r); setRejectNotes(''); setShowReject(true); };
  const doReject = async () => {
    if (rejectNotes.trim().length < 3) { toast.error('Rejection reason likho'); return; }
    try {
      await api.post(`/vendor-fund-requests/${active.id}/reject`, { review_notes: rejectNotes });
      toast.success('Rejected');
      setShowReject(false);
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-5" data-testid="admin-fund-requests-page">
      <div className="flex items-center gap-2 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Wallet size={26} weight="duotone" className="text-indigo-600" /> Vendor Fund Requests
        </h1>
        <div className="ml-auto flex gap-2 flex-wrap md:mr-14">
          {['', 'pending', 'approved', 'rejected'].map((s) => (
            <button key={s || 'all'} onClick={() => setFilter(s)}
              className={`px-3 py-1 text-xs font-semibold rounded border ${filter === s ? 'bg-indigo-600 border-indigo-600 text-white' : 'bg-white border-gray-300'}`}
              data-testid={`admin-fr-filter-${s || 'all'}`}>
              {s ? s.toUpperCase() : 'ALL'}
            </button>
          ))}
          <Button variant="outline" size="sm" onClick={load}><ArrowClockwise size={14} /> Refresh</Button>
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-sm text-gray-500">Loading...</div>
        : requests.length === 0 ? <div className="p-8 text-center text-sm text-gray-500">No requests in "{filter || 'all'}" status</div>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Vendor</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-left">Payment Mode</th>
                  <th className="px-3 py-2 text-left">Bank / Account</th>
                  <th className="px-3 py-2 text-left">Reference</th>
                  <th className="px-3 py-2 text-left">Receipt</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`admin-fr-${r.id}`}>
                    <td className="px-3 py-2 text-xs">
                      {r.deposit_date || fmtDate(r.created_at)}
                      <div className="text-[10px] text-gray-400">{fmtDate(r.created_at)}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <div className="font-semibold">{r.vendor_name}</div>
                      <div className="text-[10px] text-gray-500 capitalize">{(r.vendor_role || '').replace('_', ' ')}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-indigo-700">{fmt(r.amount)}</td>
                    <td className="px-3 py-2 text-xs">{r.payment_mode || (r.method || '').replace('_', ' ')}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.bank_name && <div className="font-semibold">{r.bank_name}</div>}
                      {r.account_info && <div className="text-gray-600 font-mono text-[11px]">{r.account_info}</div>}
                      {!r.bank_name && !r.account_info && (
                        r.account_holder ? <span>{r.account_holder} • {r.account_number}</span>
                        : r.upi_id ? <span className="font-mono">{r.upi_id}</span>
                        : <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">{r.reference_number || '—'}</td>
                    <td className="px-3 py-2 text-xs">
                      {r.screenshot_url ? (
                        <a href={`${API_BASE}${r.screenshot_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          View
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                        r.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                        r.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>{r.status?.toUpperCase()}</span>
                      {r.txn_reference && <div className="text-[10px] text-gray-500 mt-0.5">UTR: {r.txn_reference}</div>}
                      {r.review_notes && <div className="text-[10px] text-gray-500 mt-0.5 truncate max-w-[140px]">{r.review_notes}</div>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.status === 'pending' && (
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 h-7 gap-1" onClick={() => openApprove(r)} data-testid={`approve-${r.id}`}>
                            <CheckCircle size={12} /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-600 h-7 gap-1" onClick={() => openReject(r)} data-testid={`reject-${r.id}`}>
                            <XCircle size={12} /> Reject
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={showApprove} onOpenChange={setShowApprove}>
        <DialogContent>
          <DialogHeader><DialogTitle>Approve Fund Request — {active && fmt(active.amount)}</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="text-xs bg-gray-50 p-2 rounded space-y-0.5">
                <div><b>{active.vendor_name}</b> • <span className="capitalize">{(active.vendor_role || '').replace('_', ' ')}</span></div>
                {active.payment_mode && <div>Mode: <b>{active.payment_mode}</b></div>}
                {active.bank_name && <div>Bank: {active.bank_name}</div>}
                {active.account_info && <div>Account: <span className="font-mono">{active.account_info}</span></div>}
                {active.reference_number && <div>Reference: <span className="font-mono">{active.reference_number}</span></div>}
                {active.deposit_date && <div>Deposit Date: {active.deposit_date}</div>}
                {active.notes && <div className="text-gray-600">Notes: {active.notes}</div>}
                {active.screenshot_url && (
                  <div>
                    Receipt: <a href={`${API_BASE}${active.screenshot_url}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">View →</a>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-xs">Transaction Reference / UTR *</Label>
                <Input value={approveForm.txn_reference} onChange={(e) => setApproveForm({ ...approveForm, txn_reference: e.target.value })} placeholder="UTR / UPI TXN ID / Cheque No" data-testid="approve-utr" />
              </div>
              <div>
                <Label className="text-xs">Review Notes</Label>
                <textarea rows={2} className="w-full px-3 py-2 border rounded text-sm" value={approveForm.review_notes} onChange={(e) => setApproveForm({ ...approveForm, review_notes: e.target.value })} placeholder="Optional notes..." />
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 p-2 rounded">
                ℹ️ Approve karne pe vendor ledger me {fmt(active.amount)} debit ho jayega with UTR reference.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprove(false)}>Cancel</Button>
            <Button onClick={doApprove} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-approve-btn">Confirm Approval</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Fund Request</DialogTitle></DialogHeader>
          <Label className="text-xs">Reason *</Label>
          <textarea rows={3} className="w-full px-3 py-2 border rounded text-sm" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} placeholder="Rejection reason..." />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>Cancel</Button>
            <Button onClick={doReject} className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject-btn">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
